import type {
  CourseCertificateEvidenceModel,
  CourseRelevanceCategory,
  CourseRelevanceConfidence,
  ProviderAttestation,
} from "@/api/cle-api";

import { formatBoolean } from "./formatters";

/**
 * Danske navne på AI'ens relevanskategorier. Kategorierne spejler
 * Advokatsamfundets kriterier for efteruddannelse; se CourseRelevanceCategory i
 * cle-compliance-api.
 */
const RELEVANCE_CATEGORY_DA: Record<CourseRelevanceCategory, string> = {
  legal_subject: "Juridisk fagområde",
  negotiation: "Forhandling",
  management: "Ledelse",
  communication: "Kommunikation",
  rhetoric: "Retorik",
  business_understanding: "Forretningsforståelse",
  approvable_format: "Godkendt uddannelsesforløb",
  non_approvable: "Kan ikke godkendes",
  unclear: "Uafklaret",
};

const RELEVANCE_CONFIDENCE_DA: Record<CourseRelevanceConfidence, string> = {
  high: "høj sikkerhed",
  medium: "middel sikkerhed",
  low: "lav sikkerhed",
};

export const PROVIDER_ATTESTATION_DA: Record<ProviderAttestation, string> = {
  signature: "Underskrift",
  provider_stamp: "Udbyders stempel",
  other_mark: "Andet mærke",
  none: "Intet fundet",
};

/**
 * "Ledelse · middel sikkerhed". null, når der ingen vurdering er — manuelle
 * beviser og analyser fra før vurderingen fandtes. En ukendt værdi vises rå
 * frem for at forsvinde, så en ny kategori i API'et ikke skjuler vurderingen.
 */
export const formatRelevanceSummary = (
  category: CourseRelevanceCategory | null | undefined,
  confidence: CourseRelevanceConfidence | null | undefined,
): string | null => {
  if (!category) return null;
  const parts = [RELEVANCE_CATEGORY_DA[category] ?? category];
  if (confidence) {
    parts.push(RELEVANCE_CONFIDENCE_DA[confidence] ?? confidence);
  }
  return parts.join(" · ");
};

/**
 * Kriterie 3 er opfyldt af underskrift ELLER udbyderens logo, og API'et
 * rapporterer begge under ét felt (`signature`). Rækken viser derfor begge.
 */
export const formatProviderAttestationValue = (
  signature: boolean | null,
  providerLogo: boolean | null | undefined,
): string =>
  `Underskrift: ${formatBoolean(signature)} · Udbyders logo: ${formatBoolean(
    providerLogo ?? null,
  )}`;

/** AI'ens begrundelse for ét felt: en kort overskrift og selve teksten. */
export interface AiNote {
  title: string | null;
  body: string | null;
}

const toNote = (
  title: string | null | undefined,
  body: string | null | undefined,
): AiNote | null =>
  title || body ? { title: title || null, body: body || null } : null;

/** null, når der intet er at vise — så vises ikonet heller ikke. */
export const getRelevanceNote = (
  certificate: Pick<
    CourseCertificateEvidenceModel,
    "aiRelevanceCategory" | "aiRelevanceConfidence" | "aiRelevanceReasoning"
  >,
): AiNote | null =>
  toNote(
    formatRelevanceSummary(
      certificate.aiRelevanceCategory,
      certificate.aiRelevanceConfidence,
    ),
    certificate.aiRelevanceReasoning,
  );

export const getProviderAttestationNote = (
  certificate: Pick<
    CourseCertificateEvidenceModel,
    "aiProviderAttestation" | "aiProviderAttestationDescription"
  >,
): AiNote | null =>
  toNote(
    certificate.aiProviderAttestation
      ? (PROVIDER_ATTESTATION_DA[certificate.aiProviderAttestation] ??
          certificate.aiProviderAttestation)
      : null,
    certificate.aiProviderAttestationDescription,
  );
